import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  keyFromName,
  nativeSelectClass,
  useProcedures,
} from "../tasks/taskData";
import {
  stagesOf,
  type PipelineStage,
  usePipelines,
  usePipelineStages,
} from "./dealData";

// Stages of the pipelines, for the admin (task 1.7, D-036): add, rename,
// reorder, follow-up days, exit rule, procedure, won and lost. A stage
// with deals is renamed or moved, never turned off; it is removed only
// when no deal uses it. This sits inside the settings form: no nested
// form, and Enter does not submit the settings.
export const PipelineStagesSettings = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data: pipelines = [] } = usePipelines();
  const { data: allStages = [] } = usePipelineStages();
  const { data: procedures = [] } = useProcedures();
  const [selected, setSelected] = useState<number | null>(null);
  const [newName, setNewName] = useState("");
  const pipelineId = selected ?? pipelines[0]?.id;
  const stages = stagesOf(allStages, pipelineId);

  // archived deals count too: they keep their stage
  const { data: deals = [] } = useGetList<{ id: number; stage_id: number }>(
    "deals",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "id", order: "ASC" },
      filter: { pipeline_id: pipelineId },
    },
    { enabled: pipelineId != null },
  );
  const dealCount = (stage: PipelineStage) =>
    deals.filter((deal) => deal.stage_id === stage.id).length;

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    await queryClient.invalidateQueries({ queryKey: ["pipeline_stages"] });
  };

  const update = (stage: PipelineStage, data: Partial<PipelineStage>) =>
    run(() =>
      dataProvider.update("pipeline_stages", {
        id: stage.id,
        data,
        previousData: stage,
      }),
    );

  const saveText = (
    stage: PipelineStage,
    field: "name" | "exit_rule",
    value: string,
  ) => {
    const trimmed = value.trim();
    if (field === "name" && !trimmed) return;
    if (trimmed === (stage[field] ?? "")) return;
    return update(stage, { [field]: trimmed || null });
  };

  const saveDays = (stage: PipelineStage, value: string) => {
    const days = value === "" ? null : Number(value);
    if (days === stage.max_days || (days !== null && !(days > 0))) return;
    return update(stage, { max_days: days });
  };

  const move = (index: number, offset: -1 | 1) => {
    const stage = stages[index];
    const other = stages[index + offset];
    if (!other) return;
    return run(async () => {
      await dataProvider.update("pipeline_stages", {
        id: stage.id,
        data: { position: other.position },
        previousData: stage,
      });
      await dataProvider.update("pipeline_stages", {
        id: other.id,
        data: { position: stage.position },
        previousData: other,
      });
    });
  };

  const remove = (stage: PipelineStage) =>
    run(() =>
      dataProvider.delete("pipeline_stages", {
        id: stage.id,
        previousData: stage,
      }),
    );

  const add = () => {
    const name = newName.trim();
    if (!name || pipelineId == null) return;
    setNewName("");
    const position = Math.max(0, ...stages.map((stage) => stage.position)) + 1;
    return run(() =>
      dataProvider.create("pipeline_stages", {
        data: {
          pipeline_id: pipelineId,
          key: keyFromName(name),
          name,
          position,
        },
      }),
    );
  };

  const noSubmit = (event: React.KeyboardEvent) => {
    if (
      event.key === "Enter" &&
      !(event.target instanceof HTMLTextAreaElement)
    ) {
      event.preventDefault();
      (event.target as HTMLElement).blur();
    }
  };

  return (
    <div className="flex flex-col gap-4" onKeyDown={noSubmit}>
      <p className="text-sm text-muted-foreground">
        {translate("tenax.deals.stages.hint")}
      </p>
      <select
        aria-label={translate("tenax.deals.pipeline")}
        className={nativeSelectClass}
        value={pipelineId ?? ""}
        onChange={(event) => setSelected(Number(event.target.value))}
      >
        {pipelines.map((pipeline) => (
          <option key={pipeline.id} value={pipeline.id}>
            {pipeline.name}
          </option>
        ))}
      </select>

      <ul className="flex flex-col gap-3">
        {stages.map((stage, index) => {
          const count = dealCount(stage);
          return (
            <li
              key={stage.id}
              aria-label={stage.name}
              className="flex flex-col gap-2 rounded-md border p-3"
            >
              <div className="flex items-center gap-1">
                <Input
                  key={`${stage.id}-${stage.name}`}
                  defaultValue={stage.name}
                  aria-label={translate("tenax.deals.stages.name")}
                  onBlur={(event) =>
                    saveText(stage, "name", event.target.value)
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={translate("tenax.deals.stages.move_up")}
                >
                  <ArrowUp />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={index === stages.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={translate("tenax.deals.stages.move_down")}
                >
                  <ArrowDown />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={count > 0}
                  title={
                    count > 0
                      ? translate("tenax.deals.stages.in_use", {
                          smart_count: count,
                        })
                      : undefined
                  }
                  onClick={() => remove(stage)}
                  aria-label={translate("tenax.deals.stages.remove")}
                >
                  <Trash2 />
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <label className="flex items-center gap-2">
                  {translate("tenax.deals.stages.max_days")}
                  <Input
                    key={`${stage.id}-days-${stage.max_days}`}
                    type="number"
                    min={1}
                    className="w-20"
                    defaultValue={stage.max_days ?? ""}
                    onBlur={(event) => saveDays(stage, event.target.value)}
                  />
                </label>
                <label className="flex items-center gap-1">
                  <Checkbox
                    checked={stage.is_won}
                    onCheckedChange={(checked) =>
                      update(stage, { is_won: !!checked })
                    }
                  />
                  {translate("tenax.deals.stages.is_won")}
                </label>
                <label className="flex items-center gap-1">
                  <Checkbox
                    checked={stage.is_lost}
                    onCheckedChange={(checked) =>
                      update(stage, { is_lost: !!checked })
                    }
                  />
                  {translate("tenax.deals.stages.is_lost")}
                </label>
                <span className="text-muted-foreground">
                  {translate("tenax.deals.stages.in_use", {
                    smart_count: count,
                  })}
                </span>
              </div>
              <Textarea
                key={`${stage.id}-rule-${stage.exit_rule}`}
                defaultValue={stage.exit_rule ?? ""}
                placeholder={translate("tenax.deals.stages.exit_rule")}
                aria-label={translate("tenax.deals.stages.exit_rule")}
                rows={2}
                onBlur={(event) =>
                  saveText(stage, "exit_rule", event.target.value)
                }
              />
              <select
                aria-label={translate("tenax.deals.stages.procedure")}
                className={nativeSelectClass}
                value={stage.procedure_id ?? ""}
                onChange={(event) =>
                  update(stage, {
                    procedure_id: event.target.value
                      ? Number(event.target.value)
                      : null,
                  })
                }
              >
                <option value="">
                  {translate("tenax.deals.stages.no_procedure")}
                </option>
                {procedures.map((procedure) => (
                  <option key={procedure.id} value={procedure.id}>
                    {procedure.title}
                  </option>
                ))}
              </select>
            </li>
          );
        })}
      </ul>

      <div className="flex gap-2">
        <Input
          value={newName}
          placeholder={translate("tenax.deals.stages.name")}
          aria-label={translate("tenax.deals.stages.new")}
          onChange={(event) => setNewName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" disabled={!newName.trim()} onClick={add}>
          {translate("tenax.deals.stages.add")}
        </Button>
      </div>
    </div>
  );
};
